import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useTooltip from '../../../../primitives/tooltip/hooks/useTooltip';
import TooltipContext from './TooltipContext';
import { jsx as _jsx } from "react/jsx-runtime";
function isValidNumber(_) {
  return _ != null && typeof _ === 'number' && !Number.isNaN(_) && Number.isFinite(_);
}
/** Simple wrapper around useTooltip, to provide tooltip data via context. */
export default function TooltipProvider(_ref) {
  let {
    hideTooltipDebounceMs = 200,
    children
  } = _ref;
  const {
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    updateTooltip,
    hideTooltip: privateHideTooltip
  } = useTooltip(undefined);
  const debouncedHideTooltip = useRef(null);
  function cancelDeboundedHideTooltip() {
    if (debouncedHideTooltip.current) {
      debouncedHideTooltip.current.cancel();
      debouncedHideTooltip.current = null;
    }
  }
  useEffect(() => cancelDeboundedHideTooltip, []);
  const showTooltip = useRef(_ref2 => {
    let {
      svgPoint,
      index,
      datum,
      distanceX,
      distanceY,
      nearestSeriesData,
      event
    } = _ref2;
    // cancel any hideTooltip calls so it won't hide after invoking the logic below
    cancelDeboundedHideTooltip();
    const cleanDistanceX = isValidNumber(distanceX) ? distanceX : Infinity;
    const cleanDistanceY = isValidNumber(distanceY) ? distanceY : Infinity;
    const distance = Math.sqrt(cleanDistanceX ** 2 + cleanDistanceY ** 2);

    // Event is valid up here. Hasn't been nullified yet.
    // We can call event.persist() but storing in a variable seems more elegant.
    const tooltipLeft = event && 'clientX' in event ? event === null || event === void 0 ? void 0 : event.clientX : svgPoint === null || svgPoint === void 0 ? void 0 : svgPoint.x;
    const tooltipTop = event && 'clientY' in event ? event === null || event === void 0 ? void 0 : event.clientY : svgPoint === null || svgPoint === void 0 ? void 0 : svgPoint.y;
    updateTooltip(_ref3 => {
      let {
        tooltipData
      } = _ref3;
      // const currNearestDatumDistance =
      //     currData?.nearestDatum && isValidNumber(currData.nearestDatum.distance)
      //         ? currData.nearestDatum.distance
      //         : Infinity;

      return {
        tooltipOpen: true,
        tooltipLeft,
        tooltipTop,
        tooltipData: {
          // nearestDatum:
          //     (currData?.nearestDatum?.key ?? '') !== key &&
          //     currNearestDatumDistance < distance
          //         ? currData?.nearestDatum
          //         : {key, index, datum, distance},
          nearestDatum: {
            index,
            datum,
            distance
          },
          nearestSeriesData: nearestSeriesData
        }
      };
    });
  });
  const hideTooltip = useCallback(() => {
    debouncedHideTooltip.current = debounce(privateHideTooltip, hideTooltipDebounceMs);
    debouncedHideTooltip.current();
  }, [privateHideTooltip, hideTooltipDebounceMs]);
  const value = useMemo(() => ({
    tooltipOpen,
    tooltipLeft,
    tooltipTop,
    tooltipData,
    updateTooltip,
    showTooltip: showTooltip.current,
    hideTooltip
  }), [hideTooltip, tooltipData, tooltipLeft, tooltipOpen, tooltipTop, updateTooltip]);
  return /*#__PURE__*/_jsx(TooltipContext.Provider, {
    value: value,
    children: children
  });
}